import React, { Context, KeyboardEvent } from 'react';
import Socket from '../../connection/socket';
import useShotsCustomHook from '../../hooks/use-shots';
import { GraphicInterface } from '../../stores/reducers/graphics';
import { Profile } from '../../stores/reducers/invitations';
import { MediaItem } from '../../stores/reducers/media';
import { baseShot, Shot, ShotFeed } from '../../stores/reducers/shots';
import { DragItem, DropCallbackData } from '../../stores/reducers/user-interface';

export interface ShotsContextInterface {
	handleSelectShot: {
		(
			newCurrentShot: number,
			forceWrite?: boolean,
			socket?: Socket | null,
			shouldUpdateRedis?: boolean,
		): void;
	};
	feedDroppedOnCanvas: {
		(
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			feedPosition: number,
			audioOnly?: boolean,
		): Promise<null>;
	};
	removeFeedFromShot: {
		(
			deleteItem: ShotFeed,
			socket: Socket,
			isForShotEditorModal: boolean,
		): Promise<null>;
	};
	overlayDroppedOnCanvas: {
		(
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			dropAreaData: DropCallbackData,
		): Promise<null>;
	};
	titleDroppedOnCanvas: {
		(
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			dropData: DropCallbackData
		): Promise<null>;
	};
	stripFeedFromShots: {
		(
			id: number | string | null,
			type: string,
			isForShotEditorModal?: boolean,
			altId?: number | string | null,
		): void
	};
	editShotModalShot: {
		(newShotModalShot: Shot, socket: Socket | null): void;
	};
	handleAddingShot: {
		(
			newShotData: Shot
		): Promise<Shot | null>;
	};
	changeShotTitle: { (newTitle: string, shotId: number): void };
	changeShotDuration: {
		(
			newDuration: number,
			shotBeingEdited: Shot,
			durationRef: React.MutableRefObject<HTMLInputElement | null>,
			isForShotEditorModal: boolean,
			socket: Socket | null,
		): void;
	};
	playShot: { (shotId: number, socket: Socket): void };
	getMissingFeeds: { (shotFeeds: ShotFeed[]): string };
	getShotHasMediaVideo: {
		(selectedShot: Shot, currentMediaArray: MediaItem[]): boolean;
	};
	hiddenTwoUpUserProfile: { (shotFeeds: ShotFeed[]): Profile | null };
	isShotBuilderOpen: boolean;
	currentShot: Shot;
	nextShot: Shot | undefined;
	globalShotGraphics: number[];
	combinedShotGraphics: number[];
	combinedShotGraphicsIsMax: boolean;
	otherShotGraphicsIsMax: boolean;
	idsOfVideosInCanvas: string[];
	isOnCanvas: boolean;
	isNextShot: boolean;
	setNextShotValue: (value: boolean) => void;
	currentShotGraphics: GraphicInterface[];
	currentShotAutoGraphicIds: number[];
	getShotDuration: (mediaId: number, oldDuration?: number) => Promise<number>;
	lockAutoPlay: boolean;
	checkMaxOverlays: (isForShotEditorModal: boolean) => boolean;
	setFeedExpanded: (shotFeed: ShotFeed, socket: Socket, isExpanded: boolean, isForShotEditorModal: boolean) => void;
	handleSaveOnEnter: (event: KeyboardEvent<HTMLInputElement>, editFn?: () => any) => void;
	pushShotFeedToCurrentShot: (feed: ShotFeed, audioOnly?: boolean) => void;
}

export const defaultContext = {
	handleSelectShot: () => {},
	feedDroppedOnCanvas: () => {},
	removeFeedFromShot: () => {},
	overlayDroppedOnCanvas: () => {},
	stripFeedFromShots: () => {},
	editShotModalShot: () => {},
	handleAddingShot: () => {},
	changeShotTitle: () => {},
	changeShotDuration: () => {},
	playShot: () => {},
	getMissingFeeds: () => '',
	getShotHasMediaVideo: () => false,
	hiddenTwoUpUserProfile: () => null,
	isShotBuilderOpen: false,
	currentShot: baseShot,
	globalShotGraphics: [],
	combinedShotGraphics: [],
	combinedShotGraphicsIsMax: false,
	otherShotGraphicsIsMax: false,
	idsOfVideosInCanvas: [],
	isOnCanvas: false,
	isNextShot: false,
	setNextShotValue: () => {},
	currentShotGraphics: [],
	currentShotAutoGraphicIds: [],
	getShotDuration: () => Promise.resolve(0),
	lockAutoPlay: false,
	checkMaxOverlays: () => false,
	pushShotFeedToCurrentShot: () => {},
};

export const ShotsContext = React.createContext(
	defaultContext,
) as unknown as Context<ShotsContextInterface>;

export const ShotsProvider = (props: any) => {
	const useShotsHook = useShotsCustomHook();
	return (
		<ShotsContext.Provider value={useShotsHook}>
			{props.children}
		</ShotsContext.Provider>
	);
};

export default function useShots() { return React.useContext(ShotsContext); }
